import React from 'react';
import { Badge, Drawer } from 'antd';
import {
  CalendarOutlined,
  TableOutlined,
  SolutionOutlined,
  AppstoreOutlined,
  ShopOutlined,
  HistoryOutlined,
  CloudUploadOutlined,
  TagsOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  // 추후 추가될 페이지 아이콘 예시 (필요 시 주석 해제)
  // BarChartOutlined,
} from '@ant-design/icons';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, ICON_SIZE, SPACING, SIDEBAR, LAYOUT, RADIUS } from '../../constants/constants';
import { useStorage } from '../../contexts/StorageContext';
import { fetchStores } from '../../api/api';
import { storeLabel } from '../../constants/stores';
import { useIsCompact } from '../../hooks/UseIsCompact';
import { useSidebarDrawer, closeSidebarDrawer } from '../../hooks/UseSidebarDrawer';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import wordmarkNavy from '../../assets/wordmark-navy.png';
import wordmarkNavy2x from '../../assets/wordmark-navy@2x.png';
import { UpdateHistoryModal } from './UpdateHistoryModal';
import { formatVersion, loadChangelog } from './changelogData';
import { APP_VERSION } from '../../generated/version';
import { onActivateKey } from '../../utils/a11y';

// ─── 버전 · 업데이트 이력 ─────────────────────────────────────────────────────
//
// 버전은 `scripts/prebuild.js` 가 package.json 을 읽어 `src/generated/version.ts` 로 만든다.
// env 파일(`.env.*.local`)은 쓰지 않는다 — CRA 가 운영 빌드에도 로드해 API 주소를 덮어쓰는
// 통로가 되기 때문이다(2026-07-24 localhost 배포 사고). 변경 이력은 같은 스크립트가
// CHANGELOG.md 에서 만든 JSON 을 읽는다.

const CHANGELOG = loadChangelog();
const LAST_SEEN_VERSION_KEY = 'cct.lastSeenVersion';

/**
 * 마지막 확인 버전 키 — 로그인 사용자별로 나눈다. 같은 브라우저에서 계정을 바꿔도
 * 서로의 "이미 봤음" 기록을 덮지 않도록 StorageContext 의 username(로그인 응답 JWT 의 sub)으로
 * 나눈다(2026-10-21 — 예전엔 역할·매장이라 같은 매장의 ADMIN 둘이 키를 같이 썼다). 로그아웃해도
 * 키를 지우지 않는다 — 그 계정은 다음 로그인 때 자기 키를 다시 본다.
 */
export function lastSeenVersionKey(identity: string): string {
  return `${LAST_SEEN_VERSION_KEY}:${identity}`;
}

/** 역할·매장 키 접미사 — username 이 없을 때(이 배포 전 로그인 세션 · 비로그인)와 예전 키 이어 읽기용. */
export function legacyVersionIdentity({
  isSuper,
  isAdmin,
  isLoggedIn,
  store,
}: {
  isSuper: boolean;
  isAdmin: boolean;
  isLoggedIn: boolean;
  store: string | null;
}): string {
  const role = isSuper ? 'SUPER' : isAdmin ? 'ADMIN' : isLoggedIn ? 'VIEWER' : 'anonymous';
  return store ? `${role}:${store}` : role;
}

/** StorageContext 값 → 사용자별 키 접미사. 로그인 계정이 있으면 user:<아이디>, 없으면 역할·매장(비로그인은 anonymous). */
export function versionIdentity(s: {
  isSuper: boolean;
  isAdmin: boolean;
  isLoggedIn: boolean;
  store: string | null;
  username?: string | null;
}): string {
  return s.isLoggedIn && s.username ? `user:${s.username}` : legacyVersionIdentity(s);
}

const readLastSeenVersion = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const saveLastSeenVersion = (key: string, version: string) => {
  try {
    localStorage.setItem(key, version);
  } catch {
    // 저장할 수 없어도 이번 세션에서는 모달을 닫은 상태로 둔다(다음 로드에 다시 알림).
  }
};

// ─── 페이지 키 ────────────────────────────────────────────────────────────────
//
// 새 페이지 추가 시:
//   1. 여기에 키를 union 에 추가
//   2. NAV_GROUPS 에 NavItem 추가
//   3. MainApp 의 PAGE_COMPONENTS 에 컴포넌트 등록
//
export type SidebarPage =
  | 'daily'              // 가격 조회 (구 "오늘의 작업") — ①②③ 세 단계만(감사 #117)
  | 'storePrice'         // 매장가 요청 (PriceHub 매장가 요청 · 고른 카드 반영 · 시세 분류 카드, 감사 #117)
  | 'decidedPrice'       // 판매가 결정 (시장 가격 · 판매처 목록을 보고 판매가를 정해 네이버 반영 · PriceHub 보고, 감사 #341)
  | 'productRegistration' // 상품 등록 (확장팩 일괄 등록 + 검색 개별 등록, 포켓몬·원피스·디지몬)
  | 'storeProducts' // 스토어 상품 관리 (가격·재고 한 표 편집)
  | 'buyback'  // 매입 관리
  | 'buybackList' // 매입 리스트 (직원이 선정한 매입 희망 상품 목록, A4 인쇄)
  | 'syncLog'  // 반영 로그
  | 'settings'; // 매장 설정 (PriceHub 연결 · 상품 등록 기본값)
  // | 'stats'   // 통계 (예시)

// ─── 네비게이션 아이템 ────────────────────────────────────────────────────────

interface NavItem {
  key:      SidebarPage;
  icon:     React.ReactNode;
  label:    string;
  sub:      string;
  /** true 면 하단 고정 영역에 배치 (설정 등) */
  pinBottom?: boolean;
  /** true 면 ADMIN 에게만 노출 (VIEWER 는 메뉴에서 숨김) */
  adminOnly?: boolean;
}

// 그룹 구분선을 위한 섹션 타입
interface NavGroup {
  label?: string; // 섹션 라벨 (없으면 라벨 없이 구분선만)
  items:  NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    // 라벨 없는 메인 섹션
    items: [
      {
        key:   'daily',
        icon:  <CalendarOutlined />,
        label: '가격 조회',
        sub:   '아침 일 · PriceHub 가격 → 네이버 반영',
      },
      {
        // 세 화면 재설계(감사 card-controltower#456 — 사장 결정 2026-10-29): 흐름 순 — 반영 → 직접 정하기 → 요청 · 대기 확인.
        // PriceHub 확장팩 화면처럼 판매처 목록을 보며 매장 판매가를 정한다(감사 #341).
        key:   'decidedPrice',
        icon:  <DollarOutlined />,
        label: '판매가 결정',
        sub:   '타 샵 시세 보고 직접 정하기 · PriceHub 에 알림',
        adminOnly: true, // 네이버 판매가를 바꾸는 쓰기 작업이라 ADMIN 전용
      },
      {
        // 요청 전용(F3) — 네이버 가격을 <매장> 가격으로 삼아 달라고 PriceHub 에 요청하고 확정 대기를 본다.
        key:   'storePrice',
        icon:  <ShopOutlined />,
        label: '매장 가격 요청',
        sub:   '네이버 가격을 PriceHub 에 요청 · 확정 대기 보기',
      },
      {
        key:   'productRegistration',
        icon:  <CloudUploadOutlined />,
        label: '상품 등록',
        sub:   '확장팩 일괄 · 검색 개별 등록',
        adminOnly: true, // 실제 네이버 스토어에 상품을 생성하는 쓰기 작업이라 ADMIN 전용
      },
      {
        key:   'storeProducts',
        icon:  <TableOutlined />,
        label: '스토어 상품 관리',
        sub:   '가격·재고 통합 편집',
      },
      {
        key:   'buyback',
        icon:  <SolutionOutlined />,
        label: '매입 관리',
        sub:   '접수 · 준비 · 완료 관리',
        adminOnly: true, // 고객 개인정보(전화·이메일)라 ADMIN 만
      },
      {
        key:   'buybackList',
        icon:  <TagsOutlined />,
        label: '매입 리스트',
        sub:   '매입 희망 상품 · A4 인쇄',
        adminOnly: true, // 매장 데이터를 바꾸는 쓰기 작업이라 ADMIN 전용
      },
      {
        key:   'syncLog',
        icon:  <HistoryOutlined />,
        label: '반영 로그',
        sub:   'Naver 가격·재고 반영 이력',
      },
    ],
  },
  {
    items: [
      {
        key:   'settings',
        icon:  <SettingOutlined />,
        label: '설정',
        sub:   'PriceHub 연결 · 상품 등록 기본값',
        pinBottom: true,
        adminOnly: true, // 매장 ADMIN(자기 매장) / SUPER. VIEWER 는 백엔드에서도 403.
      },
    ],
  },
  // 새 섹션 추가 예시:
  // {
  //   label: '분석',
  //   items: [
  //     { key: 'stats', icon: <BarChartOutlined />, label: '통계', sub: '가격 변동 추이' },
  //   ],
  // },
];

/**
 * 관리자 전용 메뉴(adminOnly) — 메뉴에서 숨기는 것과 같은 목록. MainApp 이 조회 전용(VIEWER)에게 이 화면을 그리지 않는 데도 쓴다
 * (감사 card-controltower#672 — 화면이 열리면 첫 조회부터 403 이 인터셉터 경고 · 화면 오류로 두 번 떴다).
 */
export const ADMIN_ONLY_PAGES: ReadonlySet<SidebarPage> = new Set(
  NAV_GROUPS.flatMap((g) => g.items).filter((i) => i.adminOnly).map((i) => i.key),
);

// ─── 사이드바 고정 색상───────────────────────────────────────────────────────
// 다크/라이트 테마와 무관하게 딥네이비 고정

// ─── Props ────────────────────────────────────────────────────────────────────

interface AppSidebarProps {
  current:  SidebarPage;
  onChange: (page: SidebarPage) => void;
  /** 메뉴별 배지 건수(0/미지정이면 배지 없음). 예: 신규 매입 미처리 건수. */
  badges?:  Partial<Record<SidebarPage, number>>;
}

// ─── NavButton ────────────────────────────────────────────────────────────────

const NavButton: React.FC<{
  item:       NavItem;
  isActive:   boolean;
  onClick:    () => void;
  badgeCount?: number;
}> = ({ item, isActive, onClick, badgeCount }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      className="app-focus-ring app-focus-ring--on-dark"
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
      onKeyDown={onActivateKey(onClick)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sm,
        padding: `${SPACING.xs}px ${SPACING.sm}px`,
        borderRadius: RADIUS.lg,
        marginBottom: SPACING.xxs,
        cursor: 'pointer',
        background: isActive
          ? SIDEBAR.bgActive
          : hovered
          ? SIDEBAR.bgHover
          : 'transparent',
        transition: 'background 0.15s ease',
        userSelect: 'none',
      }}
    >
      {/* 아이콘 */}
      <span
        style={{
          fontSize: ICON_SIZE.md,
          color: isActive ? SIDEBAR.navTextActive : SIDEBAR.navText,
          width: ICON_SIZE.box,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'color 0.15s ease',
        }}
      >
        {item.icon}
      </span>

      {/* 텍스트 */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: FONT_SIZE.sm,
            fontWeight: isActive ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
            color: isActive ? SIDEBAR.navTextActive : SIDEBAR.navText,
            lineHeight: LINE_HEIGHT.snug,
            transition: 'color 0.15s ease',
          }}
        >
          {item.label}
        </div>
        <div
          style={{
            fontSize: FONT_SIZE.xs,
            color: isActive ? SIDEBAR.navSubActive : SIDEBAR.navSub,
            lineHeight: LINE_HEIGHT.tight,
            marginTop: SPACING.xxs,
            transition: 'color 0.15s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.sub}
        </div>
      </div>

      {/* 미처리 건수 배지(예: 신규 매입). 0/미지정이면 렌더 안 함. */}
      {!!badgeCount && badgeCount > 0 && (
        <Badge count={badgeCount} overflowCount={99} style={{ marginLeft: 'auto', flexShrink: 0 }} />
      )}
    </div>
  );
};

// ─── NavGroup 렌더러 ──────────────────────────────────────────────────────────

const NavGroupSection: React.FC<{
  group:   NavGroup;
  current: SidebarPage;
  onChange: (page: SidebarPage) => void;
  isFirst: boolean;
  badges?: Partial<Record<SidebarPage, number>>;
}> = ({ group, current, onChange, isFirst, badges }) => (
  <div>
    {/* 섹션 구분선 + 라벨 */}
    {!isFirst && (
      <div
        style={{
          height: 1,
          background: SIDEBAR.divider,
          margin: `${SPACING.xs}px ${SPACING.xs}px`,
        }}
      />
    )}
    {group.label && (
      <div
        style={{
          fontSize: FONT_SIZE.xs,
          color: SIDEBAR.groupLabel,
          fontWeight: FONT_WEIGHT.semibold,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          padding: `${SPACING.xs}px ${SPACING.sm}px ${SPACING.xxs}px`,
        }}
      >
        {group.label}
      </div>
    )}
    {group.items.map((item) => (
      <NavButton
        key={item.key}
        item={item}
        isActive={current === item.key}
        onClick={() => onChange(item.key)}
        badgeCount={badges?.[item.key]}
      />
    ))}
  </div>
);

// ─── SuperStoreSwitcher (SUPER 전용) ──────────────────────────────────────────
//
// 전 매장 총괄(SUPER)이 대상 매장을 고르는 스위처.
//   - '전 매장 개요'(null) → 횡단 비교 화면
//   - 특정 매장 → 그 매장으로 조회·편집(요청에 X-Store-Code 로 전달됨)
// 매장 목록은 백엔드 /api/super/stores 에서 받아 온다(코드→라벨은 storeLabel).

const SwitcherRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ icon, label, isActive, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      className="app-focus-ring app-focus-ring--on-dark"
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
      onKeyDown={onActivateKey(onClick)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sm,
        padding: `${SPACING.xs}px ${SPACING.sm}px`,
        borderRadius: RADIUS.lg,
        marginBottom: SPACING.xxs,
        cursor: 'pointer',
        background: isActive ? SIDEBAR.bgActive : hovered ? SIDEBAR.bgHover : 'transparent',
        transition: 'background 0.15s ease',
        userSelect: 'none',
      }}
    >
      <span style={{
        fontSize: ICON_SIZE.md,
        color: isActive ? SIDEBAR.navTextActive : SIDEBAR.navText,
        width: ICON_SIZE.box,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </span>
      <span style={{
        fontSize: FONT_SIZE.sm,
        fontWeight: isActive ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
        color: isActive ? SIDEBAR.navTextActive : SIDEBAR.navText,
        lineHeight: LINE_HEIGHT.snug,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {label}
      </span>
    </div>
  );
};

const SuperStoreSwitcher: React.FC<{
  superStore: string | null;
  onSelect: (store: string | null) => void;
}> = ({ superStore, onSelect }) => {
  const [stores, setStores] = React.useState<string[]>([]);

  React.useEffect(() => {
    fetchStores().then(setStores).catch(() => { /* 인터셉터가 안내 */ });
  }, []);

  return (
    <div style={{ marginBottom: SPACING.sm }}>
      <div style={{
        fontSize: FONT_SIZE.xs,
        color: SIDEBAR.groupLabel,
        fontWeight: FONT_WEIGHT.semibold,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: `${SPACING.xs}px ${SPACING.sm}px ${SPACING.xxs}px`,
      }}>
        매장 선택
      </div>
      <SwitcherRow
        icon={<AppstoreOutlined />}
        label="전 매장 개요"
        isActive={superStore === null}
        onClick={() => onSelect(null)}
      />
      {stores.map((code) => (
        <SwitcherRow
          key={code}
          icon={<ShopOutlined />}
          label={storeLabel(code)}
          isActive={superStore === code}
          onClick={() => onSelect(code)}
        />
      ))}
      <div style={{ height: 1, background: SIDEBAR.divider, margin: `${SPACING.xs}px ${SPACING.xs}px` }} />
    </div>
  );
};

// ─── AppSidebar ───────────────────────────────────────────────────────────────

/**
 * 왼쪽 고정 네비게이션 사이드바.
 *
 * 좁은 폭(<= 999px)에서는 고정 사이드바를 그리지 않고 antd `Drawer`(왼쪽, 220px) 안에
 * 같은 메뉴를 담는다 — 햄버거 버튼은 DashboardHeader 가 그리고, 열림 상태는
 * `useSidebarDrawer` 로 두 컴포넌트가 공유한다.
 *
 * 페이지 추가 방법:
 *   1. `SidebarPage` union 에 키 추가
 *   2. `NAV_GROUPS` 에 NavItem 추가 (기존 그룹 또는 새 섹션)
 *   3. `MainApp.tsx` 의 `PAGE_COMPONENTS` 에 컴포넌트 등록
 */
export const AppSidebar: React.FC<AppSidebarProps> = ({ current, onChange, badges }) => {
  const { isAdmin, isSuper, isLoggedIn, store, superStore, username, setSuperStore } = useStorage();
  const t = useThemeTokens();
  // DashboardLayout·DashboardHeader 와 같은 훅(같은 matchMedia 질의)을 씀.
  const compact = useIsCompact();
  const { drawerOpen, closeDrawer } = useSidebarDrawer();

  // prebuild 가 package.json version 으로 `src/generated/version.ts` 를 만든다. 값이
  // 없으면(구버전 빌드·생성 전 tsc) 예전 표기(v1.0)를 유지하되, 새 버전 알림은 켜지 않는다.
  const appVersion = APP_VERSION ? formatVersion(APP_VERSION) : '';
  const displayVersion = appVersion || 'v1.0';
  // 마지막 확인 버전은 로그인 사용자별 키로 읽고 쓴다(계정을 바꿔도 서로 덮지 않는다).
  const identity = { isSuper, isAdmin, isLoggedIn, store, username };
  const userKey = lastSeenVersionKey(versionIdentity(identity));
  // 계정 키가 아직 없으면 예전 역할·매장 키를 이어 읽는다 — 바꾼 직후 이미 본 버전 모달이 다시 뜨지 않게.
  const legacyKey = lastSeenVersionKey(legacyVersionIdentity(identity));
  const readSeen = () => readLastSeenVersion(userKey) ?? (legacyKey !== userKey ? readLastSeenVersion(legacyKey) : null);
  const [lastSeenVersion, setLastSeenVersion] = React.useState<string | null>(readSeen);
  const hasNewVersion = !!appVersion && lastSeenVersion !== appVersion;
  // 첫 진입(새 버전 최초 로드)에 한 번만 자동으로 연다 — 닫으면 아래 closeHistory 가 저장한다.
  const [historyOpen, setHistoryOpen] = React.useState<boolean>(
    () => !!appVersion && readSeen() !== appVersion,
  );

  const closeHistory = () => {
    setHistoryOpen(false);
    if (appVersion) {
      saveLastSeenVersion(userKey, appVersion);
      setLastSeenVersion(appVersion);
    }
  };

  // SUPER 가 '전 매장 개요'(superStore=null)를 보고 있을 때는 개별 페이지 내비를 숨기고
  // 매장 스위처만 노출한다. 매장을 고르면(superStore!=null) 그 매장의 페이지 내비가 나온다.
  const showPageNav = !isSuper || superStore !== null;

  // adminOnly 아이템은 VIEWER 에게 숨긴다(백엔드 GET 도 ADMIN 전용이라 눌러도 403).
  const visible = (i: NavItem) => !i.adminOnly || isAdmin;

  // pinBottom 아이템은 분리하여 하단에 고정
  const mainGroups   = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => visible(i) && !i.pinBottom),
  })).filter((g) => g.items.length > 0);

  const bottomItems  = NAV_GROUPS.flatMap((g) => g.items.filter((i) => visible(i) && i.pinBottom));

  // 좁은 폭에서 메뉴를 고르면 Drawer 를 닫는다(안 닫으면 새 화면 위를 계속 덮는다).
  const handleSelect = (page: SidebarPage) => {
    onChange(page);
    closeDrawer();
  };

  // 넓은 폭으로 돌아오면 Drawer 자체가 사라지므로 열림 상태도 닫아 둔다 — 안 그러면
  // 다시 좁아졌을 때 메뉴가 저절로 열려 있다.
  React.useEffect(() => {
    if (!compact) closeDrawer();
  }, [compact, closeDrawer]);

  // 로그아웃·세션 만료(401)로 셸이 언마운트될 때 모듈 전역 열림 상태를 남기지 않는다 —
  // 다음 로그인(좁은 폭)에서 메뉴가 열린 채로 시작하는 걸 막는다(2026-10-20 리뷰).
  React.useEffect(() => () => closeSidebarDrawer(), []);

  // 메뉴 본문 — 넓은 폭에선 고정 사이드바, 좁은 폭에선 Drawer 안에 같은 것을 담는다.
  const menuBody = (
    <>
      {/* ── 로고 ── */}
      <div
        style={{
          padding: `${SPACING.lg}px ${SPACING.md}px ${SPACING.md}px`,
          borderBottom: `1px solid ${SIDEBAR.border}`,
          flexShrink: 0,
        }}
      >
        {/* 워드마크(심벌 + CCT, 코덱 제작 2026-10-20)는 장식 — 이름은 아래 글자가 읽힌다. 248×32 원본을
            24 높이로 줄여 220px 사이드바 · Drawer 안쪽 폭에 맞춘다. */}
        <img
          src={wordmarkNavy}
          srcSet={`${wordmarkNavy2x} 2x`}
          width={186}
          height={24}
          alt=""
          style={{ display: 'block', marginBottom: SPACING.xs }}
        />
        <div
          style={{
            fontSize: FONT_SIZE.md,
            fontWeight: FONT_WEIGHT.bold,
            color: SIDEBAR.logoText,
            letterSpacing: '0.02em',
            lineHeight: LINE_HEIGHT.tight,
          }}
        >
          CardControlTower
        </div>
      </div>

      {/* ── 메인 네비게이션 ── */}
      <nav
        style={{
          flex: 1,
          padding: `${SPACING.sm}px ${SPACING.xs}px`,
          overflowY: 'auto',
        }}
      >
        {isSuper && (
          <SuperStoreSwitcher superStore={superStore} onSelect={setSuperStore} />
        )}
        {showPageNav && mainGroups.map((group, i) => (
          <NavGroupSection
            key={i}
            group={group}
            current={current}
            onChange={handleSelect}
            isFirst={i === 0}
            badges={badges}
          />
        ))}
      </nav>

      {/* ── 하단 고정 아이템 (설정 등 pinBottom) ── */}
      {bottomItems.length > 0 && (
        <div
          style={{
            padding: `${SPACING.xs}px ${SPACING.xs}px`,
            borderTop: `1px solid ${SIDEBAR.divider}`,
          }}
        >
          {bottomItems.map((item) => (
            <NavButton
              key={item.key}
              item={item}
              isActive={current === item.key}
              onClick={() => handleSelect(item.key)}
            />
          ))}
        </div>
      )}

      {/* ── 버전 ── */}
      <div
        style={{
          padding: `${SPACING.sm}px ${SPACING.md}px`,
          borderTop: `1px solid ${SIDEBAR.divider}`,
          fontSize: FONT_SIZE.xs,
          color: SIDEBAR.versionText,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.xs,
        }}
      >
        <span>{`Card Control Tower ${displayVersion}`}</span>
        <button
          type="button"
          // 새 버전 점 배지는 눈으로만 보인다 — 화면 낭독기에도 같은 뜻이 가도록 이름에 싣는다(감사 card-controltower#78).
          aria-label={hasNewVersion ? '업데이트 이력(새 버전)' : '업데이트 이력'}
          title={hasNewVersion ? '업데이트 이력(새 버전)' : '업데이트 이력'}
          onClick={() => setHistoryOpen(true)}
          style={{
            width: ICON_SIZE.xxl,
            height: ICON_SIZE.xxl,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: SIDEBAR.versionText,
            cursor: 'pointer',
            flexShrink: 0,
            marginLeft: 'auto',
          }}
        >
          <Badge dot={hasNewVersion} color={t.danger} offset={[2, -2]}>
            {/* 색은 아이콘에 직접 — Badge 래퍼가 글자색을 antd 기본(어두운 색)으로 덮어 라이트 테마 네이비 바탕에서
                1.13:1 로 묻혔다(2026-10-21 코덱 화면 점검). */}
            <InfoCircleOutlined style={{ fontSize: ICON_SIZE.md, color: SIDEBAR.versionText }} />
          </Badge>
        </button>
      </div>
    </>
  );

  // 모달은 Drawer 밖에 둔다 — 좁은 폭에서 Drawer 가 닫혀 있어도 새 버전 알림·이력이 열려야 한다.
  const historyModal = (
    <UpdateHistoryModal
      open={historyOpen}
      onClose={closeHistory}
      entries={CHANGELOG.entries}
      loadFailed={CHANGELOG.failed}
      isNew={hasNewVersion}
    />
  );

  // 좁은 폭 — 고정 사이드바를 그리지 않는다(본문 왼쪽 여백도 DashboardLayout 이 0 으로 둔다).
  // Drawer 는 처음 열릴 때 내용을 렌더하므로, 닫혀 있는 동안엔 문서에 메뉴가 없다.
  if (compact) {
    return (
      <>
        <Drawer
          placement="left"
          size={LAYOUT.sidebarWidth}
          open={drawerOpen}
          onClose={closeDrawer}
          closable={false}
          styles={{
            body: {
              padding: 0,
              background: SIDEBAR.bg,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {menuBody}
        </Drawer>
        {historyModal}
      </>
    );
  }

  return (
    <>
      <div
        style={{
          width: LAYOUT.sidebarWidth,
          minHeight: '100vh',
          background: SIDEBAR.bg,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 300,
          borderRight: `1px solid ${SIDEBAR.border}`,
        }}
      >
        {menuBody}
      </div>
      {historyModal}
    </>
  );
};
